import React from 'react';
import { createPortal } from 'react-dom';
import type { BannerConfig, PageConfig } from './types.ts';
import { Accordion, ColorField, ImageField, RangeField, Segmented } from './fields.tsx';
import { WhatsAppLinkField } from './WhatsAppLinkField.tsx';
import styles from './gerador.module.scss';
import { applyStyleFromBanner, createBanner } from './utils.ts';
import { AlignCenter, AlignLeft, AlignRight, ChevronDown, ChevronUp, Copy, Plus, Trash2 } from 'lucide-react';

type PageFieldsProps = {
    page: PageConfig;
    onChange: (page: PageConfig) => void;
};

export const PageFields: React.FC<PageFieldsProps> = ({ page, onChange }) => (
    <div className={styles.card} data-tour="pagina">
        <h2>Sua página</h2>
        <p className={styles.cardLead}>Foto no topo, fundo atrás dos banners.</p>

        <ImageField
            label="Foto de perfil"
            value={page.profileImage}
            onChange={(profileImage) => onChange({ ...page, profileImage })}
            hint="Melhor em 1280 × 1600. Vai no topo, colada nas bordas."
            tour="foto"
            maxEdge={1600}
            previewRatio="1280 / 1600"
        />

        <div className={styles.colorGroup}>
            <ColorField
                label="Cor de fundo"
                value={page.backgroundColor}
                onChange={(backgroundColor) => onChange({ ...page, backgroundColor })}
            />
        </div>

        <div data-tour="fundo">
            <ImageField
                label="Imagem de fundo"
                value={page.backgroundImage}
                onChange={(backgroundImage) => onChange({ ...page, backgroundImage })}
                optional
                hint="Cobre a tela. A cor aparece nas bordas e enquanto carrega."
                maxEdge={1920}
                compact
            />
        </div>
    </div>
);

type BannerListProps = {
    page: PageConfig;
    selectedId: string | null;
    onChange: (page: PageConfig) => void;
    onSelect: (id: string) => void;
};

export const BannerList: React.FC<BannerListProps> = ({ page, selectedId, onChange, onSelect }) => {
    const move = (index: number, direction: -1 | 1) => {
        const next = index + direction;
        if (next < 0 || next >= page.banners.length) return;
        const banners = [...page.banners];
        const [item] = banners.splice(index, 1);
        banners.splice(next, 0, item);
        onChange({ ...page, banners });
    };

    const remove = (id: string) => {
        if (page.banners.length <= 1) return;
        const banners = page.banners.filter((b) => b.id !== id);
        onChange({ ...page, banners });
        if (selectedId === id) onSelect(banners[0].id);
    };

    const add = () => {
        const source = page.banners.find((b) => b.id === selectedId) ?? page.banners[page.banners.length - 1];
        const banner = source ? applyStyleFromBanner(source, createBanner()) : createBanner();
        onChange({ ...page, banners: [...page.banners, banner] });
        onSelect(banner.id);
    };

    return (
        <div className={styles.card} data-tour="banners">
            <div className={styles.cardHead}>
                <div>
                    <h2>Banners</h2>
                    <p className={styles.cardLead}>Toque para editar. Setas mudam a ordem.</p>
                </div>
                <button type="button" className={styles.primaryBtn} onClick={add}>
                    <Plus size={16} />
                    Adicionar
                </button>
            </div>

            <ul className={styles.bannerList}>
                {page.banners.map((banner, index) => (
                    <li key={banner.id}>
                        <button
                            type="button"
                            className={`${styles.bannerListItem} ${selectedId === banner.id ? styles.bannerListItemActive : ''}`}
                            onClick={() => onSelect(banner.id)}
                        >
                            <span
                                className={styles.bannerSwatch}
                                style={{ background: banner.backgroundColor, color: banner.textColor }}
                            >
                                {banner.title.slice(0, 1) || 'B'}
                            </span>
                            <span className={styles.bannerListMeta}>
                                <strong>{banner.title || 'Sem título'}</strong>
                                <small>{banner.coupon.enabled ? 'Abre cupom' : banner.href ? 'Abre link' : 'Sem destino'}</small>
                            </span>
                        </button>
                        <div className={styles.bannerListActions}>
                            <button type="button" aria-label="Mover para cima" onClick={() => move(index, -1)} disabled={index === 0}>
                                <ChevronUp size={16} />
                            </button>
                            <button
                                type="button"
                                aria-label="Mover para baixo"
                                onClick={() => move(index, 1)}
                                disabled={index === page.banners.length - 1}
                            >
                                <ChevronDown size={16} />
                            </button>
                            <button
                                type="button"
                                aria-label="Excluir banner"
                                onClick={() => remove(banner.id)}
                                disabled={page.banners.length <= 1}
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

type BannerFieldsProps = {
    banner: BannerConfig;
    onChange: (banner: BannerConfig) => void;
    onReplicateStyle: () => void;
    canReplicate: boolean;
};

export const BannerFields: React.FC<BannerFieldsProps> = ({
    banner,
    onChange,
    onReplicateStyle,
    canReplicate,
}) => {
    const [replicated, setReplicated] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const coupon = banner.coupon;
    const patchCoupon = (partial: Partial<BannerConfig['coupon']>) =>
        onChange({ ...banner, coupon: { ...coupon, ...partial } });

    React.useEffect(() => {
        if (!confirmOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setConfirmOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [confirmOpen]);

    const handleConfirmReplicate = () => {
        onReplicateStyle();
        setConfirmOpen(false);
        setReplicated(true);
        window.setTimeout(() => setReplicated(false), 1800);
    };

    return (
        <div className={styles.card} data-tour="banner-edit">
            <h2>Banner selecionado</h2>
            <p className={styles.cardLead}>Texto, cores e para onde o toque leva.</p>

            <p className={styles.sectionTitle}>Texto</p>
            <label className={styles.field} data-tour="titulo">
                <span className={styles.fieldLabel}>Título</span>
                <input
                    type="text"
                    value={banner.title}
                    onChange={(e) => onChange({ ...banner, title: e.target.value })}
                    placeholder="Ex.: WHATSAPP"
                />
            </label>

            <div className={styles.bannerMetaRow}>
                <Segmented
                    compact
                    label="Alinhamento"
                    value={banner.titleAlign}
                    onChange={(titleAlign) => onChange({ ...banner, titleAlign })}
                    options={[
                        { value: 'left', label: <AlignLeft size={16} />, title: 'Esquerda' },
                        { value: 'center', label: <AlignCenter size={16} />, title: 'Centro' },
                        { value: 'right', label: <AlignRight size={16} />, title: 'Direita' },
                    ]}
                />
                <label className={styles.checkInline}>
                    <input
                        type="checkbox"
                        checked={banner.uppercase}
                        onChange={(e) => onChange({ ...banner, uppercase: e.target.checked })}
                    />
                    Caixa alta
                </label>
            </div>
            <RangeField
                label="Tamanho da fonte"
                value={banner.fontSize}
                min={14}
                max={56}
                onChange={(fontSize) => onChange({ ...banner, fontSize })}
            />

            <p className={styles.sectionTitle}>Cores</p>
            <div data-tour="cores" className={styles.colorGroup}>
                <ColorField
                    label="Cor do banner"
                    value={banner.backgroundColor}
                    onChange={(backgroundColor) => onChange({ ...banner, backgroundColor })}
                />
                <ColorField
                    label="Cor do texto"
                    value={banner.textColor}
                    onChange={(textColor) => onChange({ ...banner, textColor })}
                />
            </div>

            <p className={styles.sectionTitle}>Destino</p>
            <div data-tour="link">
                <WhatsAppLinkField
                    label="Link do banner"
                    value={banner.href}
                    onChange={(href) => onChange({ ...banner, href })}
                    placeholder="https://wa.me/55..."
                    disabled={coupon.enabled}
                    hint={
                        coupon.enabled
                            ? 'Com o cupom ligado, o toque abre a janela do cupom.'
                            : 'Cole o endereço ou use o ícone do WhatsApp.'
                    }
                />
            </div>

            <div className={styles.couponBlock} data-tour="cupom">
                <label className={styles.checkRow}>
                    <input
                        type="checkbox"
                        checked={coupon.enabled}
                        onChange={(e) => patchCoupon({ enabled: e.target.checked })}
                    />
                    Este banner abre um cupom
                </label>
                <p className={styles.hint}>
                    A pessoa vê o código, copia e vai para o site.
                </p>

                {coupon.enabled ? (
                    <div className={styles.couponFields}>
                        <div className={styles.twoCols}>
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Nome da marca</span>
                                <input
                                    type="text"
                                    value={coupon.brand}
                                    onChange={(e) => patchCoupon({ brand: e.target.value })}
                                    placeholder="Minha Loja"
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Texto pequeno</span>
                                <input
                                    type="text"
                                    value={coupon.eyebrow}
                                    onChange={(e) => patchCoupon({ eyebrow: e.target.value })}
                                    placeholder="Cupom exclusivo"
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Código</span>
                                <input
                                    type="text"
                                    value={coupon.code}
                                    onChange={(e) => patchCoupon({ code: e.target.value })}
                                    placeholder="VIBE10"
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Desconto</span>
                                <input
                                    type="text"
                                    value={coupon.discount}
                                    onChange={(e) => patchCoupon({ discount: e.target.value })}
                                    placeholder="10% off"
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Sem código ainda</span>
                                <input
                                    type="text"
                                    value={coupon.emptyText}
                                    onChange={(e) => patchCoupon({ emptyText: e.target.value })}
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Texto do botão</span>
                                <input
                                    type="text"
                                    value={coupon.ctaLabel}
                                    onChange={(e) => patchCoupon({ ctaLabel: e.target.value })}
                                />
                            </label>
                        </div>
                        <span className={styles.hint}>Código vazio mostra o texto de “em breve”.</span>
                        <WhatsAppLinkField
                            label="Link do site / WhatsApp"
                            value={coupon.siteUrl}
                            onChange={(siteUrl) => patchCoupon({ siteUrl })}
                            placeholder="https://..."
                        />

                        <Accordion title="Cores da janela do cupom">
                            <div className={styles.colorGroup}>
                                <ColorField label="Fundo (topo)" value={coupon.modalBg} onChange={(modalBg) => patchCoupon({ modalBg })} />
                                <ColorField label="Fundo (base)" value={coupon.modalBg2} onChange={(modalBg2) => patchCoupon({ modalBg2 })} />
                                <ColorField label="Texto" value={coupon.modalText} onChange={(modalText) => patchCoupon({ modalText })} />
                                <ColorField label="Caixa do código" value={coupon.boxBg} onChange={(boxBg) => patchCoupon({ boxBg })} />
                                <ColorField label="Código" value={coupon.codeColor} onChange={(codeColor) => patchCoupon({ codeColor })} />
                                <ColorField label="Botão" value={coupon.buttonBg} onChange={(buttonBg) => patchCoupon({ buttonBg })} />
                                <ColorField label="Texto do botão" value={coupon.buttonText} onChange={(buttonText) => patchCoupon({ buttonText })} />
                            </div>
                        </Accordion>
                    </div>
                ) : null}
            </div>

            <Accordion title="Espaçamento e borda" tour="espaco">
                <div className={styles.twoCols}>
                    <RangeField
                        label="Altura mínima"
                        value={banner.minHeight}
                        min={44}
                        max={140}
                        onChange={(minHeight) => onChange({ ...banner, minHeight })}
                    />
                    <RangeField
                        label="Arredondamento"
                        value={banner.borderRadius}
                        min={0}
                        max={40}
                        onChange={(borderRadius) => onChange({ ...banner, borderRadius })}
                    />
                    <RangeField
                        label="Espaço interno vertical"
                        value={banner.paddingY}
                        min={4}
                        max={48}
                        onChange={(paddingY) => onChange({ ...banner, paddingY })}
                    />
                    <RangeField
                        label="Espaço interno lateral"
                        value={banner.paddingX}
                        min={8}
                        max={48}
                        onChange={(paddingX) => onChange({ ...banner, paddingX })}
                    />
                    <RangeField
                        label="Margem acima"
                        value={banner.marginTop}
                        min={0}
                        max={40}
                        onChange={(marginTop) => onChange({ ...banner, marginTop })}
                    />
                    <RangeField
                        label="Margem abaixo"
                        value={banner.marginBottom}
                        min={0}
                        max={40}
                        onChange={(marginBottom) => onChange({ ...banner, marginBottom })}
                    />
                    <RangeField
                        label="Espaço entre letras"
                        value={banner.letterSpacing}
                        min={0}
                        max={10}
                        onChange={(letterSpacing) => onChange({ ...banner, letterSpacing })}
                    />
                    <RangeField
                        label="Espessura da borda"
                        value={banner.borderWidth}
                        min={0}
                        max={6}
                        onChange={(borderWidth) => onChange({ ...banner, borderWidth })}
                    />
                </div>
                {banner.borderWidth > 0 ? (
                    <div className={styles.colorGroup}>
                        <ColorField
                            label="Cor da borda"
                            value={banner.borderColor}
                            onChange={(borderColor) => onChange({ ...banner, borderColor })}
                        />
                    </div>
                ) : null}
                <Segmented
                    label="Sombra"
                    value={banner.shadow}
                    onChange={(shadow) => onChange({ ...banner, shadow })}
                    options={[
                        { value: 'none', label: 'Nenhuma' },
                        { value: 'soft', label: 'Suave' },
                        { value: 'strong', label: 'Forte' },
                    ]}
                />
            </Accordion>
            <button
                type="button"
                className={styles.replicateBtn}
                onClick={() => setConfirmOpen(true)}
                disabled={!canReplicate || replicated}
            >
                <Copy size={16} />
                {replicated ? 'Estilo aplicado nos outros banners' : 'Aplicar estilo nos outros banners'}
            </button>
            <span className={styles.hint}>
                Copia cores, fonte, alinhamento e espaçamento. Não altera título, link nem o conteúdo do cupom.
            </span>
            {confirmOpen
                ? createPortal(
                    <div
                        className={styles.confirmOverlay}
                        onClick={() => setConfirmOpen(false)}
                        role="presentation"
                    >
                        <div
                            className={styles.confirmModal}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="replicate-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 id="replicate-title">Aplicar estilo nos outros banners?</h3>
                            <p>
                                Isso copia cores, fonte, alinhamento e espaçamento deste banner para os demais.
                                Título, link e conteúdo do cupom não mudam.
                            </p>
                            <div className={styles.confirmActions}>
                                <button type="button" className={styles.ghostBtn} onClick={() => setConfirmOpen(false)}>
                                    Cancelar
                                </button>
                                <button type="button" className={styles.primaryBtn} onClick={handleConfirmReplicate}>
                                    Aplicar
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </div>
    );
};
